/**
 * Centralized React Query keys for the Customer portal. Using factory
 * functions keeps invalidation type-safe and consistent across hooks.
 *
 * Customer-scoped keys include a context discriminator so that switching
 * between B2C and B2B organisations never surfaces cached data from a
 * different context.
 */
import { customerContextKey } from "./customerContext";

export const queryKeys = {
  dashboard: ["dashboard"] as const,
  auth: {
    me: ["auth", "me"] as const,
    contexts: ["auth", "contexts"] as const,
  },
  requests: {
    all: () => ["requests", customerContextKey()] as const,
    list: (params?: Record<string, unknown>) =>
      ["requests", customerContextKey(), "list", params ?? {}] as const,
    detail: (requestId: string) => ["requests", customerContextKey(), requestId, "detail"] as const,
    status: (requestId: string) => ["requests", customerContextKey(), requestId, "status"] as const,
    eligibleVendors: (requestId: string) =>
      ["requests", customerContextKey(), requestId, "eligibleVendors"] as const,
    /** Prefix (no includeWithdrawn) matches all bid-list variants on invalidate. */
    bids: (requestId: string, includeWithdrawn?: boolean) =>
      includeWithdrawn === undefined
        ? (["requests", customerContextKey(), requestId, "bids"] as const)
        : (["requests", customerContextKey(), requestId, "bids", includeWithdrawn] as const),
  },
  orders: {
    all: () => ["orders", customerContextKey()] as const,
    list: (params?: Record<string, unknown>) =>
      ["orders", customerContextKey(), "list", params ?? {}] as const,
    detail: (orderId: string) => ["orders", customerContextKey(), orderId, "detail"] as const,
  },
  workOrders: {
    all: () => ["workOrders", customerContextKey()] as const,
    byOrder: (orderId: string) => ["workOrders", customerContextKey(), "order", orderId] as const,
  },
  payments: {
    all: () => ["payments", customerContextKey()] as const,
    orderList: (orderId: string) => ["payments", customerContextKey(), "order", orderId] as const,
    detail: (paymentId: string) => ["payments", customerContextKey(), "detail", paymentId] as const,
  },
  wallet: {
    all: () => ["wallet", customerContextKey()] as const,
    balance: () => ["wallet", customerContextKey(), "balance"] as const,
    transactions: (params?: Record<string, unknown>) =>
      ["wallet", customerContextKey(), "transactions", params ?? {}] as const,
    topup: (id: string) => ["wallet", customerContextKey(), "topup", id] as const,
    spendingLimits: () => ["wallet", customerContextKey(), "spendingLimits"] as const,
  },
  invoices: {
    detail: (invoiceId: string) => ["invoices", customerContextKey(), "detail", invoiceId] as const,
  },
  projectLocations: {
    all: () => ["projectLocations", customerContextKey()] as const,
    list: () => ["projectLocations", customerContextKey(), "list"] as const,
    detail: (savedLocationId: string) =>
      ["projectLocations", customerContextKey(), savedLocationId, "detail"] as const,
  },
  services: {
    publicList: ["services", "publicList"] as const,
    publicDetail: (serviceId: string) => ["services", "publicDetail", serviceId] as const,
  },
  geography: {
    publicCountries: ["geography", "publicCountries"] as const,
    publicMunicipalities: (countryId?: string) =>
      ["geography", "publicMunicipalities", countryId ?? ""] as const,
    publicDistricts: (municipalityId: string) =>
      ["geography", "publicDistricts", municipalityId] as const,
  },
  catalog: {
    publicDeliveryTimeSlots: ["catalog", "publicDeliveryTimeSlots"] as const,
  },
  notifications: {
    all: () => ["notifications", customerContextKey()] as const,
    list: (params?: Record<string, unknown>) =>
      ["notifications", customerContextKey(), "list", params ?? {}] as const,
  },
  invitations: {
    all: () => ["invitations", customerContextKey()] as const,
    list: () => ["invitations", customerContextKey(), "list"] as const,
  },
  orgRoles: {
    all: () => ["orgRoles", customerContextKey()] as const,
    list: () => ["orgRoles", customerContextKey(), "list"] as const,
    assignablePermissions: () =>
      ["orgRoles", customerContextKey(), "assignablePermissions"] as const,
  },
};
